'use client';

import { Badge } from '@ahafood/ui/components/badge';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@ahafood/ui/components/carousel';
import { Image } from '@ahafood/ui/components/image';
import { Skeleton } from '@ahafood/ui/components/skeleton';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, formatCurrency } from '@ahm/common-helpers';
import React from 'react';
import type { SoloMeal } from '@/api/use-get-menu-ws';

interface SoloMealCarouselProps {
  soloMeals: SoloMeal[];
  currentItemId: number;
  onItemClick: (soloMeal: SoloMeal) => void;
  isLoading: boolean;
}

export function SoloMealCarousel({
  soloMeals,
  currentItemId,
  onItemClick,
  isLoading,
}: SoloMealCarouselProps) {
  if (isLoading) {
    return (
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-neutral-90 mb-3">
          Món SoloMeal khác
        </h3>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex-none w-32">
              <Skeleton className="aspect-square w-full rounded-2xl mb-2" />
              <Skeleton className="h-4 w-full mb-1" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (soloMeals.length === 0) {
    return null;
  }

  return (
    <div className="mb-4 w-[calc(100vw)] bg-blue-10 -mx-4">
      <Carousel
        opts={{
          align: 'start',
          dragFree: true,
        }}
        className="w-full"
      >
        <CarouselContent className="ml-2 gap-3 p-2">
          {soloMeals.map((soloMeal) => {
            const isActive = soloMeal._id === currentItemId;
            const imageSrc =
              soloMeal.image_preview_url && soloMeal.image_preview_url !== ''
                ? soloMeal.image_preview_url
                : soloMeal.image_thumb_url ||
                  `${assetPrefix}/static/images/empty-store-bot.webp`;

            return (
              <CarouselItem
                key={soloMeal._id}
                className="basis-auto pl-0 relative"
              >
                {isActive && (
                  <Badge className="z-50 absolute top-0 left-0 rounded-none rounded-br rounded-tl bg-blue-40 text-white">
                    Đang chọn
                  </Badge>
                )}
                <button
                  type="button"
                  onClick={() => onItemClick(soloMeal)}
                  className={cn(
                    isActive ? 'border-blue-40' : 'border-none',
                    'flex gap-3 border-2 rounded-lg p-2 w-52 bg-white flex-shrink-0 h-fit'
                  )}
                  disabled={isActive}
                >
                  <div className="relative size-12 aspect-square shrink-0">
                    <img
                      src={imageSrc}
                      alt={soloMeal.name}
                      className="size-full object-cover rounded-md"
                    />
                    {soloMeal.is_ai_image && (
                      <Badge className="absolute top-1 right-1 size-6 bg-white/50 hover:bg-white/90 text-white border-none shadow-sm p-1">
                        <Image
                          fill
                          className="p-0.5"
                          src={`${assetPrefix}/static/icons/white-ai.svg`}
                          alt={'AI'}
                        />
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-col gap-0 flex-1 min-w-0 h-fit">
                    <span className="font-normal text-base text-neutral-90 truncate text-left">
                      {soloMeal.name}
                    </span>

                    {soloMeal.discounted_price !== soloMeal.price ? (
                      <div className="text-right">
                        <span className="line-through truncate text-xs text-foreground">
                          {formatCurrency(soloMeal.price)}
                        </span>
                        <span className="text-primary text-sm font-semibold ml-1">
                          {' '}
                          {formatCurrency(soloMeal.discounted_price)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-primary text-sm font-semibold ml-1 text-right">
                        {formatCurrency(soloMeal.discounted_price)}
                      </span>
                    )}
                  </div>
                </button>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
